import React, { useEffect, useRef, useState } from 'react';
import { runEvalForSkill, EVAL_BAR } from '../../services/protectBuild';
import { EYEBROW, LABEL, NOTE } from './typeScale';
import Pill from './Pill';

/**
 * Does it do what it says, measured before it is allowed further.
 *
 * <h3>Why the walk cannot pass this</h3>
 *
 * <p>The Build gate's `at_least_one_eval_ready` reads, per skill, whether a
 * completed eval suite exists for THAT skill and whether its score reaches the
 * bar. Another skill's eval does not count. Nothing in Protect ever ran one, so
 * no agent built here could reach Decide — which is exactly where the first
 * end-to-end walk stopped.</p>
 *
 * <h3>What the number means, and whose it is</h3>
 *
 * <p>The bar is the gate's own constant, not a figure chosen here, and each score
 * is read back off the skill because that is where the run writes it and where
 * the gate later reads it. A skill is shown as ready only on the same test the
 * gate applies.</p>
 */
export default function EvalsAsk({ agentId, state, onRefresh, onAnswer, busy = false }) {
  const skills = state?.skills || [];
  const providers = state?.providers || [];
  const canGenerate = Boolean(state?.canGenerate);
  const anyReady = Boolean(state?.anyReady);

  /* Read on open. The page loads this state once, when the walk is resumed,
     and the step BEFORE this one creates the agent's protection policy — so on
     the walk this step was built for, the policy always exists and was always
     missing from the list. Found by driving it, not by a test: the policy sat
     on the agent while this showed only the four bridge proxies, and an
     unmeasured policy skill never leaves build
     (promoteEvalReadyBuildSkills:348), so it could never be reviewed.

     Keyed on the agent, NOT on `state` — the refresh replaces `state`, so
     re-reading on that would ask again for every answer it got. */
  const asked = useRef(null);
  useEffect(() => {
    if (!agentId || asked.current === agentId) return;
    asked.current = agentId;
    onRefresh?.();
    // onRefresh deliberately omitted from deps: the ref above already guards against
    // asking twice for the same agent, and the parent hands down a fresh closure on
    // every render, so including it would re-run the effect continuously. (No disable
    // directive here — the project's ESLint config doesn't register
    // react-hooks/exhaustive-deps, and naming an unregistered rule is itself a
    // build-breaking error. Same trap as Modal.jsx:107 and AgentMarketplacePageV2.jsx:211.)
  }, [agentId]);

  /* Held as a position, not as an id. The platform's built-in judge has no connector id, so an
     id-keyed selection cannot tell "the built-in judge" from "nothing chosen yet" — and the row
     the step lands on is the one its number will be attributed to. */
  const [pick, setPick] = useState(0);
  const [running, setRunning] = useState(null);
  const [problem, setProblem] = useState(null);

  const chosen = providers[pick] || providers[0] || null;
  const pct = (n) => `${Math.round(n * 100)}%`;

  const run = async (skillId) => {
    setRunning(skillId);
    setProblem(null);
    const res = await runEvalForSkill(agentId, skillId, chosen);
    if (!res.ok) setProblem(res.error);
    await onRefresh?.();
    setRunning(null);
  };

  return (
    <div style={COL}>
      {/* Nothing here can generate one. Said plainly, because the consequence is
          that the agent cannot leave Build at all — not that a button is
          missing. */}
      {!canGenerate ? (
        <div style={PANEL}>
          <span style={EYEBROW}>No model to write the tests</span>
          <p style={{ ...NOTE, marginTop: '7px' }}>
            Evals are written by a model, and this tenant has none configured. Until one
            is, its skills cannot be measured and the agent cannot move past this stage.
            An administrator configures one for the tenant.
          </p>
        </div>
      ) : null}

      {/* Shown whenever there is a judge to show, not only when there is a choice between them.
          It used to appear only above one, and the step committed to whichever row the list
          returned first — so the judge was neither visible nor chosen. Driven live, that first
          row was a connector the platform had marked unusable: no model judged the skill, the
          score fell back to a configuration check, and the screen attributed it to the model
          that never ran. One judge is still the fact the number depends on. */}
      {canGenerate ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
          <label htmlFor="ev-prov" style={LABEL}>Written by</label>
          <select id="ev-prov" className="p7-input" value={pick}
                  onChange={(e) => setPick(Number(e.target.value))} style={{ maxWidth: '280px' }}>
            {providers.map((p, i) => (
              <option key={p.id || 'platform'} value={i}>{p.name}</option>
            ))}
          </select>
        </div>
      ) : null}

      <div style={PANEL}>
        <span style={EYEBROW}>Its skills, against the {pct(EVAL_BAR)} bar</span>
        {skills.length === 0 ? (
          <p style={{ ...NOTE, marginTop: '7px' }}>
            It has no skills to measure yet. Give it some in the step before this one.
          </p>
        ) : (
          <div style={{ marginTop: '9px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {skills.map((sk) => (
              <div key={sk.id} style={{ display: 'flex', alignItems: 'center',
                                        gap: '10px', flexWrap: 'wrap' }}>
                <span style={{ font: 'var(--p7-text-body)', fontFamily: 'var(--p7-font-sans)',
                               color: 'var(--p7-ink)', minWidth: '180px' }}>
                  {sk.name}
                </span>
                <Pill tone={sk.ready ? 'allow' : 'hold'}>
                  {sk.score === null ? 'not measured'
                    : `${pct(sk.score)}${sk.ready ? '' : ` · below ${pct(EVAL_BAR)}`}`}
                </Pill>
                <button type="button" className="p7-btn p7-btn--sm"
                        disabled={busy || !canGenerate || running !== null}
                        onClick={() => run(sk.id)}>
                  {running === sk.id ? 'Measuring…'
                    : sk.score === null ? 'Measure it' : 'Measure again'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {problem ? (
        <p style={{ ...NOTE, color: 'var(--p7-deny-ink)' }}>{problem}</p>
      ) : null}

      <p style={NOTE}>
        {anyReady
          ? `At least one skill is at or above ${pct(EVAL_BAR)}, which is what this stage `
            + 'needs. Skills still below it stay behind until they are measured again.'
          : `Nothing has reached ${pct(EVAL_BAR)} yet. A skill that has not is left behind `
            + 'when the agent moves on, and cannot be reviewed or put to work.'}
      </p>

      <div>
        <button type="button" className="p7-btn p7-btn--primary" disabled={busy}
                onClick={() => onAnswer({ anyReady, measured: skills.filter((s) => s.ready).length })}>
          Continue
        </button>
      </div>
    </div>
  );
}

const COL = { display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' };
const PANEL = {
  border: '1px solid var(--p7-line)', borderRadius: 'var(--p7-radius-control)',
  padding: '11px 13px', background: 'var(--p7-surface-sunken)',
};
